// src/components/wall/TeamLoaderBoardComponent.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import useIndividualStream from "../shared/hooks/useIndividualSocket";
import axios from "axios";
import { API_ENDPOINT } from "../../lib/api-url"
import TimerMini from "../shared/ui/timer-mini/TimerMini";

// staggered entrance per row (unchanged)
const rowVariants = {
  hidden: { opacity: 0, y: 14, filter: "blur(3px)" },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

// static assets per team (unchanged)
const TEAM_META = {
  bull: {
    id: "bull",
    name: "BULL MASTERS",
    logo: "/teams/bullmaster-logo.webp",
    banner: "/teams/bullmasters-baner.svg",
  },
  iron: {
    id: "iron",
    name: "IIRON BEARS",
    logo: "/teams/ironbears-logo.webp",
    banner: "/teams/ironbears-baner.svg",
  },
  alpha: {
    id: "alpha",
    name: "ALPHA WOLVES",
    logo: "/teams/alphawolves-logo.webp",
    banner: "/teams/alphawolves-baner.svg",
  },
  eagles: {
    id: "eagles",
    name: "GOLDEN EAGLES",
    logo: "/teams/goldeneagles-logo.webp",
    banner: "/teams/goldeneagles-baner.svg",
  },
};

// API team.name -> our TEAM_META key (unchanged)
const NAME_TO_META = {
  BULLMASTERS: "bull",
  IRONBEARS: "iron",
  ALPHAWOLVES: "alpha",
  GOLDENEAGLES: "eagles",
};

// --- parsing helpers (unchanged) ---
function ensureObject(maybe) {
  if (!maybe) return null;
  if (typeof maybe === "string") {
    try {
      return JSON.parse(maybe);
    } catch {
      return null;
    }
  }
  return maybe;
}

function parseRiskEvent(frame) {
  const obj = ensureObject(frame) || frame;
  const re = obj?.RiskEvent || {};
  const login = String(re.login || "").trim();
  const equity = Number(re.new_value ?? NaN);

  let balance = Number(re.corrected_value ?? NaN);
  if (!Number.isFinite(balance)) {
    const m = String(re.message || "").match(/Balance=\$([0-9.\-]+)/i);
    if (m) balance = Number(m[1]);
  }

  const initial = Number(re.initial_balance ?? NaN);

  return {
    login,
    equity: Number.isFinite(equity) ? equity : null,
    balance: Number.isFinite(balance) ? balance : null,
    initial: Number.isFinite(initial) ? initial : null,
    timestamp: re.timestamp || null,
  };
}

// Aggregate socket accounts -> team stats (actives only).
// Profit % = (ΣEquity - ΣInitial) / ΣInitial * 100  (ΣInitial from socket initial_balance)
function aggregateTeams(accountsByLogin, loginToTeam, activeMetaSet) {
  const agg = {}; // teamId -> { equitySum, balanceSum, initialSum }

  Object.entries(accountsByLogin).forEach(([login, acc]) => {
    const teamId = loginToTeam[login];
    if (!teamId) return;
    if (!activeMetaSet.has(teamId)) return;

    if (!agg[teamId])
      agg[teamId] = { equitySum: 0, balanceSum: 0, initialSum: 0 };

    if (acc.equity != null) agg[teamId].equitySum += acc.equity;
    if (acc.balance != null) agg[teamId].balanceSum += acc.balance;
    if (acc.initial != null) agg[teamId].initialSum += acc.initial;
  });

  const list = Array.from(activeMetaSet).map((metaId) => {
    const meta = TEAM_META[metaId];
    const a = agg[metaId] || { equitySum: 0, balanceSum: 0, initialSum: 0 };
    const { equitySum, balanceSum, initialSum } = a;

    const profit =
      initialSum !== 0
        ? Math.round(((equitySum - initialSum) / Math.abs(initialSum)) * 100)
        : 0;

    const points = Math.max(0, Math.round(equitySum / 100 + profit * 10));

    return {
      ...meta,
      equity: Math.round(equitySum),
      balance: Math.round(balanceSum),
      profit,
      points,
    };
  });

  return [...list].sort(
    (a, b) => (b.balance ?? -Infinity) - (a.balance ?? -Infinity)
  );
}

// Default export with prop for account types to include (default "main")
export default function CustomerTeamLeaderBoard({
  accountTypes = "main", // string | string[]
}) {
  const accountTypeSet = useMemo(() => {
    const arr = Array.isArray(accountTypes) ? accountTypes : [accountTypes];
    return new Set(arr.map((t) => String(t || "").toLowerCase()));
  }, [accountTypes]);

  const [loginToTeam, setLoginToTeam] = useState({});
  const [activeMetaSet, setActiveMetaSet] = useState(new Set());
  const [accountsByLogin, setAccountsByLogin] = useState({});

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.wallTeams.sabziTeams);
        const arr = Array.isArray(res?.data?.data) ? res.data.data : [];

        const actives = arr.filter(
          (t) => String(t?.status || "").toLowerCase() === "active"
        );

        const nextMap = {};
        const activeMetaIds = new Set();

        actives.forEach((team) => {
          const metaId = NAME_TO_META[String(team?.name || "").toUpperCase()];
          if (!metaId) return;
          activeMetaIds.add(metaId);

          const accounts = Array.isArray(team.team_accounts)
            ? team.team_accounts
            : [];

          accounts
            .filter((acc) =>
              accountTypeSet.has(String(acc?.type || "").toLowerCase())
            )
            .forEach((acc) => {
              const login = String(acc?.login || "").trim();
              if (login) nextMap[login] = metaId;
            });
        });

        if (isMounted) {
          setLoginToTeam(nextMap);
          setActiveMetaSet(activeMetaIds);
        }
      } catch (e) {
        console.error("Failed to fetch teams map:", e);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [accountTypeSet]); // re-build map if desired types change

  const { lastItem } = useIndividualStream({
    url: "ws://193.23.118.210:3004/team?token=client_secret_token",
    token:
      (typeof localStorage !== "undefined" && localStorage.getItem("token")) ||
      "",
    enabled: true,
  });

  useEffect(() => {
    if (!lastItem) return;
    const evt = parseRiskEvent(lastItem);
    if (!evt?.login) return;
    console.log("result from socket : ", lastItem);
    if (!loginToTeam[evt.login]) return;

    setAccountsByLogin((prev) => {
      const prevAcc = prev[evt.login] || {};
      return {
        ...prev,
        [evt.login]: {
          ...prevAcc,
          login: evt.login,
          equity: evt.equity ?? prevAcc.equity ?? null,
          balance: evt.balance ?? prevAcc.balance ?? null,
          initial: evt.initial ?? prevAcc.initial ?? null,
          ts: evt.timestamp || Date.now(),
        },
      };
    });
  }, [lastItem, loginToTeam]);

  const teams = useMemo(
    () => aggregateTeams(accountsByLogin, loginToTeam, activeMetaSet),
    [accountsByLogin, loginToTeam, activeMetaSet]
  );

  return (
    <div
      style={{
        scale: "1.2",
        direction:"ltr"
      }}
      className="w-screen flex h-screen items-center justify-center flex-col relative"
    >
      <div className=" absolute left-[35%] bottom-[42%] z-20 -translate-x-1/2 -translate-y-1/2">
        <TimerMini />
      </div>
      <div>
        <h2 className="text-[#FFFFFF] font-bold text-[26px] flex items-center justify-center">
          رده بندی گروهی
        </h2>
        <div className="relative mt-2 h-20 w-[40rem]">
          <div className="absolute inset-x-20 top-0 h-[3px] w-3/4 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md" />
          <div className="absolute inset-x-20 top-0 h-px w-3/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_8px_#ffffff]" />
          <div className="absolute inset-x-60 top-0 h-[6px] w-1/4 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-md" />
          <div className="absolute inset-x-60 top-0 h-px w-1/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_10px_#ffffff]" />
          <div className="absolute inset-x-44 top-0 h-[2px] w-2/4 bg-gradient-to-r from-transparent via-sky-100/60 to-transparent blur-[2px]" />
          <div className="pointer-events-none absolute inset-0 h-full w-full [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]" />
        </div>
      </div>

      {/* list (stagger + FLIP) — untouched motion */}
      <motion.div layout className="w-full flex flex-col items-center">
        {teams.map((team, index) => {
          const mtClass =
            index === 0 ? "" : index === 1 ? "mt-[50px]" : "mt-[60px]";
          return (
            <motion.div
              key={team.id}
              layout
              custom={index}
              variants={rowVariants}
              initial="hidden"
              animate="visible"
              transition={{
                layout: {
                  type: "spring",
                  stiffness: 520,
                  damping: 38,
                  mass: 0.6,
                },
              }}
              className={`team-banner-container ${mtClass} w-[700px] h-[70px] flex items-center justify-center relative will-change-transform transform-gpu`}
            >
              {index === 0 && (
                <div className="absolute z-[1] top-[-24px]">
                  <div className="header-container mr-[220px] relative">
                    <img src="/teams/team-table-header.svg" alt="" />
                    <div className="header-text-container flex gap-[42px] absolute z-[4] top-[50%] left-[65%] translate-x-[-50%] translate-y-[-50%] w-[320px]">
                      <span className="text-[#FFFFFF] text-[10px] font-bold">
                        درصد سود و زیان
                      </span>
                      <span className="text-[#FFFFFF] text-[10px] font-bold">
                        اکوییتی
                      </span>
                      <span className="text-[#FFFFFF] text-[10px] font-bold">
                        بالانس
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <img
                style={{ dropShadow: "0px 4px 4px 0px #00000040" }}
                className={
                  team.id === "bull"
                    ? "w-[80px] absolute right-[80px] top-[-15px] z-[2]"
                    : team.id === "iron"
                    ? "w-[120px] absolute right-[60px] top-[-5px] z-[2]"
                    : team.id === "alpha"
                    ? "w-[150px] h-[95px] absolute right-[45px] top-[-20px] z-[2]"
                    : "w-[155px] absolute right-[36px] top-[-30px] z-[2]"
                }
                src={team.logo}
                alt={team.name}
              />

              <img
                className="w-[600px] absolute z-[1]"
                src={team.banner}
                alt=""
              />

              <div
                className={`text-container ${
                  team.id === "iron" || team.id === "alpha" ? "mt-[16px]" : ""
                }  relative z-[3] flex items-center justify-center w-full gap-[32px] mr-[24px]`}
              >
                <span
                  className={`text-[14px] font-bold w-[50px] text-center ${
                    team.profit < 0 ? "text-[#FF4747]" : "text-[#40BF6A]"
                  }`}
                >
                  {team.profit}%
                </span>

                <span className="text-[#FFFFFF] w-[50px] text-nowrap text-[14px] font-bold">
                  {team.equity.toLocaleString()} $
                </span>
                <span className="text-[#FFFFFF] w-[50px] text-nowrap text-[14px] font-bold">
                  {team.balance.toLocaleString()} $
                </span>
                <span
                  style={{ fontFamily: "Etemad" }}
                  className="text-[#FFFFFF] text-[12px] font-normal w-[150px] text-left"
                >
                  {team.name}
                </span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
